import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ClientOfferViewUnit,
  OfferVersionWithSnapshot,
  RecordedUnitDecisions,
} from '@ooh/contracts';
import { useCurrentUser } from '../../plugs/current-user';
import {
  noopAccept,
  READ_ONLY_ACCEPT_MUTATION,
  SharedClientOfferContent,
} from './shared-client-offer-content';
import type { OpenClientUnitDetail } from './shared-client-offer-content';
import { buildEnrichedUnitById } from './lib/build-enriched-unit-lookup';
import { buildReadOnlyDecisionsFromRecorded } from './lib/seed-selections-from-prefill';

// ─── Post-acceptance read-only content (client-offer-interactive-view /
// Post-Acceptance Read-Only Client View) ───────────────────────────────────────
// Deliberately bypasses useClientOfferSelections entirely — that hook manages
// in-progress, writable session state (sessionStorage persistence included),
// which a finalized read-only view has none of. Hydrates decisions/selectedPeriods
// directly from the server's recordedDecisions instead (buildReadOnlyDecisionsFromRecorded).

export interface ClientOfferReadOnlyContentProps {
  versionId: string;
  versionNumber: number;
  publishedAt: Date;
  acceptedAt: Date;
  snapshot: OfferVersionWithSnapshot;
  enrichedUnits: ClientOfferViewUnit[];
  defaultDateFrom: Date | null;
  defaultDateTo: Date | null;
  recordedDecisions: RecordedUnitDecisions | undefined;
  onOpenUnitDetail: OpenClientUnitDetail;
}

export function ClientOfferReadOnlyContent({
  versionId,
  versionNumber,
  publishedAt,
  acceptedAt,
  snapshot,
  enrichedUnits,
  defaultDateFrom,
  defaultDateTo,
  recordedDecisions,
  onOpenUnitDetail,
}: ClientOfferReadOnlyContentProps) {
  const { user } = useCurrentUser();
  const { t, i18n } = useTranslation('offers');

  const enrichedById = useMemo(() => buildEnrichedUnitById(enrichedUnits), [enrichedUnits]);

  const { decisions, selectedPeriods } = useMemo(
    () =>
      buildReadOnlyDecisionsFromRecorded(
        recordedDecisions,
        enrichedById,
        defaultDateFrom,
        defaultDateTo,
        i18n.language,
      ),
    [recordedDecisions, enrichedById, defaultDateFrom, defaultDateTo, i18n.language],
  );

  return (
    <SharedClientOfferContent
      versionNumber={versionNumber}
      publishedAt={publishedAt}
      acceptedAt={acceptedAt}
      rejectedAt={null}
      diff={null}
      onAccept={noopAccept}
      acceptMutation={READ_ONLY_ACCEPT_MUTATION}
      clientName={snapshot.snapshotData.clientName}
      offerTitle={snapshot.snapshotData.title}
      readOnly
      finalizedHint={t('clientOfferReviewV2.readOnlyRecordedHint')}
      decisions={decisions}
      selectedPeriods={selectedPeriods}
      snapshotUnits={snapshot.snapshotData.units}
      enrichedUnitsById={enrichedById}
      showSupplierIdentity={false}
      campaignFrom={defaultDateFrom}
      campaignTo={defaultDateTo}
      accessMode="Zalogowany (SSO)"
      loggedInEmail={user?.email ?? ''}
      onOpenUnitDetail={(mediaUnitId) =>
        onOpenUnitDetail(mediaUnitId, { fromVersionId: versionId })
      }
    />
  );
}
